import { startTransition, useDeferredValue, useMemo, useState } from "react";
import {
  amenityPacks,
  createDefaultScoreConfig,
  DEFAULT_AMENITY_PACK_ID,
  getDefaultPackTypes,
  isPackCustomized,
  MAX_RECOMMENDED_SELECTED_TYPES,
  normalizeScoreConfig,
} from "../../shared/amenityPacks";
import { allPlaceTypes, placeTypeCategories, placeTypeLabelMap } from "../../shared/placeTypes";
import {
  formatDistance,
  MAX_RADIUS_METERS,
  MIN_RADIUS_METERS,
  priceLevelOptions,
  RADIUS_STEP_METERS,
} from "../../shared/filters";
import type {
  AmenityPackId,
  ScoreConfig,
  SearchFilters,
} from "../../shared/types";

const ratingOptions: Array<{ label: string; value: number }> = [
  { label: "★ 3+", value: 3 },
  { label: "★ 3.5+", value: 3.5 },
  { label: "★ 4+", value: 4 },
  { label: "★ 4.5+", value: 4.5 },
];

const BAND_STEP = 0.05;

interface FilterPanelProps {
  activePackId: AmenityPackId;
  filters: SearchFilters;
  radiusMeters: number;
  scoreConfig: ScoreConfig;
  onActivePackChange: (nextPackId: AmenityPackId) => void;
  onFiltersChange: (nextFilters: SearchFilters) => void;
  onRadiusChange: (nextRadius: number) => void;
  onScoreConfigChange: (nextScoreConfig: ScoreConfig) => void;
  onApply: () => void;
  isBusy?: boolean;
}

function formatPercent(weight: number): string {
  return `${Math.round(weight * 100)}%`;
}

export function FilterPanel({
  activePackId,
  filters,
  radiusMeters,
  scoreConfig,
  onActivePackChange,
  onFiltersChange,
  onRadiusChange,
  onScoreConfigChange,
  onApply,
  isBusy = false,
}: FilterPanelProps) {
  const [typeQuery, setTypeQuery] = useState("");
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const deferredTypeQuery = useDeferredValue(typeQuery);
  const normalizedQuery = deferredTypeQuery.trim().toLowerCase();
  const normalizedScoreConfig = useMemo(
    () => normalizeScoreConfig(scoreConfig, activePackId),
    [activePackId, scoreConfig],
  );
  const packCustomized = isPackCustomized(
    activePackId,
    filters.includedTypes,
    normalizedScoreConfig,
  );
  const resolvedBands = normalizedScoreConfig.bands.map((band) => ({
    ...band,
    radiusMeters: Math.round(radiusMeters * band.fraction),
  }));
  const activePack = amenityPacks.find((pack) => pack.id === activePackId) ?? null;

  const filteredCategories = placeTypeCategories
    .map((category) => ({
      ...category,
      types: category.types.filter((type) => {
        if (!normalizedQuery) {
          return true;
        }

        return (
          type.label.toLowerCase().includes(normalizedQuery) ||
          type.value.toLowerCase().includes(normalizedQuery)
        );
      }),
    }))
    .filter((category) => category.types.length > 0);

  function updateFilters(updater: (current: SearchFilters) => SearchFilters) {
    startTransition(() => {
      onFiltersChange(updater(filters));
    });
  }

  function updateScoreConfig(updater: (current: ScoreConfig) => ScoreConfig) {
    startTransition(() => {
      onScoreConfigChange(normalizeScoreConfig(updater(normalizedScoreConfig), activePackId));
    });
  }

  function toggleStringValue(key: "includedTypes" | "priceLevels", value: string) {
    updateFilters((current) => {
      const collection = new Set((current[key] as string[] | undefined) ?? []);

      if (collection.has(value)) {
        if (key === "includedTypes" && collection.size === 1) {
          return current;
        }

        collection.delete(value);
      } else {
        collection.add(value);
      }

      return {
        ...current,
        [key]: [...collection],
      };
    });
  }

  function applyAmenityPack(packId: AmenityPackId) {
    const nextPackId = packId === "custom" ? DEFAULT_AMENITY_PACK_ID : packId;

    onActivePackChange(nextPackId);
    onFiltersChange({
      ...filters,
      includedTypes: getDefaultPackTypes(nextPackId),
    });
    onScoreConfigChange(createDefaultScoreConfig(nextPackId));
  }

  function resetPackDefaults() {
    applyAmenityPack(activePackId === "custom" ? DEFAULT_AMENITY_PACK_ID : activePackId);
  }

  function updateBandFraction(index: 0 | 1, nextFraction: number) {
    updateScoreConfig((current) => {
      const nextBands = current.bands.map((band) => ({ ...band })) as ScoreConfig["bands"];
      nextBands[index].fraction = nextFraction;
      return { bands: nextBands };
    });
  }

  function updateBandWeight(index: 0 | 1 | 2, nextWeightPercent: number) {
    updateScoreConfig((current) => {
      const nextBands = current.bands.map((band) => ({ ...band })) as ScoreConfig["bands"];
      nextBands[index].weight = nextWeightPercent / 100;
      return { bands: nextBands };
    });
  }

  return (
    <div className="panel">
      <div className="panel__section">
        <p className="eyebrow">Area</p>
        <h2>Search radius</h2>
        <label className="field">
          <span className="field__label">{formatDistance(radiusMeters)}</span>
          <input
            aria-label="Search radius"
            className="slider"
            max={MAX_RADIUS_METERS}
            min={MIN_RADIUS_METERS}
            onChange={(event) => onRadiusChange(Number(event.target.value))}
            step={RADIUS_STEP_METERS}
            type="range"
            value={radiusMeters}
          />
        </label>

        <div className="advanced-scoring">
          <button
            aria-expanded={advancedOpen}
            className="ghost-button ghost-button--block"
            onClick={() => setAdvancedOpen((current) => !current)}
            type="button"
          >
            {advancedOpen ? "Hide advanced scoring" : "Advanced scoring"}
          </button>
          {advancedOpen ? (
            <div className="advanced-scoring__panel">
              <div className="advanced-scoring__header">
                <div>
                  <p className="eyebrow">Scoring model</p>
                  <h3>Distance bands</h3>
                </div>
                {packCustomized ? <span className="pill pill--muted">Customized</span> : null}
              </div>
              <label className="field">
                <span className="field__label">
                  Inner band {formatDistance(resolvedBands[0].radiusMeters)}
                </span>
                <input
                  aria-label="Inner score band"
                  className="slider"
                  max={normalizedScoreConfig.bands[1].fraction - BAND_STEP}
                  min={BAND_STEP}
                  onChange={(event) => updateBandFraction(0, Number(event.target.value))}
                  step={BAND_STEP}
                  type="range"
                  value={normalizedScoreConfig.bands[0].fraction}
                />
              </label>
              <label className="field">
                <span className="field__label">
                  Middle band {formatDistance(resolvedBands[1].radiusMeters)}
                </span>
                <input
                  aria-label="Middle score band"
                  className="slider"
                  max={1 - BAND_STEP}
                  min={normalizedScoreConfig.bands[0].fraction + BAND_STEP}
                  onChange={(event) => updateBandFraction(1, Number(event.target.value))}
                  step={BAND_STEP}
                  type="range"
                  value={normalizedScoreConfig.bands[1].fraction}
                />
              </label>
              <p className="results__subtle">
                Outer band follows the search radius: {formatDistance(resolvedBands[2].radiusMeters)}.
              </p>
              <div className="advanced-scoring__weights">
                {resolvedBands.map((band, index) => (
                  <label className="field" key={`${band.fraction}-${index}`}>
                    <span className="field__label">
                      {formatDistance(band.radiusMeters)} weight {formatPercent(band.weight)}
                    </span>
                    <input
                      aria-label={`${formatDistance(band.radiusMeters)} weight`}
                      className="slider"
                      max={90}
                      min={5}
                      onChange={(event) => updateBandWeight(index as 0 | 1 | 2, Number(event.target.value))}
                      step={5}
                      type="range"
                      value={Math.round(band.weight * 100)}
                    />
                  </label>
                ))}
              </div>
              <p className="results__subtle">
                Near to far:{" "}
                {resolvedBands
                  .map((band) => `${formatDistance(band.radiusMeters)} × ${formatPercent(band.weight)}`)
                  .join(", ")}
              </p>
              <button className="ghost-button ghost-button--block" onClick={resetPackDefaults} type="button">
                Reset to pack defaults
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="panel__section">
        <p className="eyebrow">Score the map</p>
        <h2>Minimum rating</h2>
        <p className="field__hint">Any rating unless you choose a floor below.</p>
        <div aria-label="Minimum rating" className="rating-chip-row" role="group">
          {ratingOptions.map((option) => {
            const isActive = filters.minRating === option.value;

            return (
              <button
                aria-pressed={isActive}
                className={`rating-toggle${isActive ? " rating-toggle--active" : ""}`}
                key={option.label}
                onClick={() =>
                  updateFilters((current) => ({
                    ...current,
                    minRating: current.minRating === option.value ? undefined : option.value,
                  }))
                }
                type="button"
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="panel__section">
        <p className="eyebrow">Spend</p>
        <h2>Price levels</h2>
        <div aria-label="Price levels" className="price-level-row" role="group">
          {priceLevelOptions.map((option) => (
            <button
              aria-pressed={filters.priceLevels?.includes(option.value) ?? false}
              className={`price-toggle${
                filters.priceLevels?.includes(option.value) ? " price-toggle--active" : ""
              }`}
              key={option.value}
              onClick={() => toggleStringValue("priceLevels", option.value)}
              type="button"
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="panel__section panel__section--types">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Investment thesis</p>
            <h2>Amenity packs</h2>
          </div>
          <span className="pill">{filters.includedTypes.length} selected</span>
        </div>
        <p className="catalog-helper-copy">
          Start from a multifamily lens, then adjust the exact Google place types below.
        </p>
        <div className="preset-grid">
          {amenityPacks.map((pack) => {
            const isActive = activePackId === pack.id;

            return (
              <button
                aria-pressed={isActive}
                className={`preset-card${isActive ? " preset-card--active" : ""}`}
                key={pack.id}
                onClick={() => applyAmenityPack(pack.id)}
                type="button"
              >
                <span className="preset-card__title">{pack.label}</span>
                <span className="preset-card__copy">{pack.description}</span>
              </button>
            );
          })}
        </div>
        <div className="selected-type-strip">
          <span className="pill pill--muted">
            {activePack?.label ?? "Custom mix"}
            {packCustomized && activePackId !== "custom" ? " · Customized" : ""}
          </span>
        </div>
        <div className="selected-type-strip">
          {filters.includedTypes.slice(0, 8).map((type) => {
            const label = placeTypeLabelMap.get(type) ?? type;

            return (
              <button
                aria-label={`Remove ${label}`}
                className="selected-type-pill"
                disabled={filters.includedTypes.length === 1}
                key={type}
                onClick={() => toggleStringValue("includedTypes", type)}
                type="button"
              >
                <span>{label}</span>
                <span aria-hidden="true" className="selected-type-pill__close">
                  x
                </span>
              </button>
            );
          })}
          {filters.includedTypes.length > 8 ? (
            <span className="pill pill--muted">+{filters.includedTypes.length - 8} more</span>
          ) : null}
        </div>
        {filters.includedTypes.length > MAX_RECOMMENDED_SELECTED_TYPES ? (
          <p className="warning-banner">
            More than {MAX_RECOMMENDED_SELECTED_TYPES} place types will slow the snapshot because
            each type adds three Aggregate count requests.
          </p>
        ) : null}
        <div className="type-catalog-toggle-row">
          <button
            className="ghost-button type-catalog-toggle"
            onClick={() => setCatalogOpen((current) => !current)}
            type="button"
          >
            {catalogOpen ? "Close full catalog" : "Browse full catalog"}
          </button>
          <button className="ghost-button type-catalog-toggle" onClick={resetPackDefaults} type="button">
            Reset pack defaults
          </button>
        </div>
        <div className="panel__footer">
          <button className="primary-button" disabled={isBusy} onClick={onApply} type="button">
            {isBusy ? "Refreshing…" : "Apply filters"}
          </button>
        </div>
      </div>

      {catalogOpen ? (
        <div className="catalog-modal">
          <button
            aria-label="Close amenity catalog"
            className="catalog-modal__backdrop"
            onClick={() => setCatalogOpen(false)}
            type="button"
          />
          <div className="catalog-modal__card">
            <div className="catalog-modal__header">
              <div>
                <p className="eyebrow">Amenity catalog</p>
                <h2>Browse Google place types</h2>
              </div>
              <div className="catalog-modal__header-actions">
                <span className="pill">{filters.includedTypes.length} selected</span>
                <button className="ghost-button" onClick={() => setCatalogOpen(false)} type="button">
                  Done
                </button>
              </div>
            </div>
            <label className="field">
              <input
                autoFocus
                className="text-input"
                onChange={(event) => setTypeQuery(event.target.value)}
                placeholder="Search the full Table A catalog"
                type="search"
                value={typeQuery}
              />
            </label>
            <div className="catalog-modal__summary selected-type-strip">
              {filters.includedTypes.slice(0, 12).map((type) => {
                const label = allPlaceTypes.find((option) => option.value === type)?.label ?? type;

                return (
                  <button
                    aria-label={`Remove ${label}`}
                    className="selected-type-pill"
                    disabled={filters.includedTypes.length === 1}
                    key={type}
                    onClick={() => toggleStringValue("includedTypes", type)}
                    type="button"
                  >
                    <span>{label}</span>
                    <span aria-hidden="true" className="selected-type-pill__close">
                      x
                    </span>
                  </button>
                );
              })}
              {filters.includedTypes.length > 12 ? (
                <span className="pill pill--muted">+{filters.includedTypes.length - 12} more</span>
              ) : null}
            </div>
            <div className="type-catalog">
              {filteredCategories.map((category) => (
                <details
                  className="type-group"
                  key={category.id}
                  open={
                    Boolean(normalizedQuery) ||
                    category.types.some((type) => filters.includedTypes.includes(type.value))
                  }
                >
                  <summary>
                    <span>{category.label}</span>
                    <span>{category.types.length}</span>
                  </summary>
                  <div className="type-group__items">
                    {category.types.map((type) => (
                      <label className="toggle-row" key={type.value}>
                        <input
                          checked={filters.includedTypes.includes(type.value)}
                          onChange={() => toggleStringValue("includedTypes", type.value)}
                          type="checkbox"
                        />
                        <span>{type.label}</span>
                      </label>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
